import styles from './AuthModal.module.scss';
import ValidateButton from '../UI/ValidateButton';
import Close from '@src/assets/Close.png';
import { Formik, Form } from 'formik';
import useRegistrationStore from '@src/stores/registrationStore';
import Input from '../UI/Input';
import { useState } from 'react';
import { forgotPassword } from '@src/api/Auth';
import { useMutation } from '@tanstack/react-query';
import { handleApiError } from '@src/utilities/functions';
import toast from 'react-hot-toast';
import Loader from '../UI/Loader';

type AuthModalProps = {
    show: boolean;
    onValidate: (value: boolean) => void;
    onLogin: ({login, password}: {login: string, password: string}) => void;
    isLoading: boolean;
}

export default function AuthModal({show, onValidate, onLogin, isLoading}: AuthModalProps) {
    const [hidePasswords, setHidePasswords] = useState<boolean>(true);
    const { registration } = useRegistrationStore();

    const { mutate, isPending } = useMutation({
        mutationKey: ['forgotPassword'],
        mutationFn: () => forgotPassword(registration.person.basic.email),
        onSuccess: () => {
            toast.success('Un email contenant un lien de réinitialisation de votre mot de passe vous a été envoyé à l\'adresse électronique renseignée.', { duration: 5000 });
        },
        onError: (error: any) => {
            handleApiError(error)
        }
    })

    if (!show) {
        return null;
    }

    return (
        <div className={styles.modalContainer}>
            <div className={styles.modal}>
                <div className={styles.close} onClick={() => onValidate(true)}>
                    <img src={Close} alt="Close" width={14} height={14} />
                </div>
                <p className={styles.title}>Connexion</p>
                <p className={styles.description}>Merci de vous connecter pour poursuivre votre inscription.</p>
                <Formik
                    initialValues={{ email: registration.person.basic.email, password: registration.person.basic.password }}
                    onSubmit={values => {
                        onLogin({login: values.email, password: values.password})
                    }}
                >
                    {({ handleChange, handleSubmit, values, errors, isValid, touched, handleBlur }) => {
                        const handleKeyDown = (event: React.KeyboardEvent) => {
                            if (event.key === 'Enter' && isValid && values.email && values.password) {
                                event.preventDefault();
                                handleSubmit();
                            }
                        };
                        return(
                            <Form className={styles.inputContainer} id="loginForm" onKeyDown={handleKeyDown}>
                                <Input
                                    title="Adresse email/login"
                                    width="100%"
                                    value={values.email}
                                    onChange={handleChange('email')}
                                    placeholder="Email/login"
                                    type="text"
                                    disabled
                                    showCheckIcon
                                    clearInput={() => handleChange('email')('')}
                                    hasError={(touched.email && errors.email) as boolean}
                                    onBlur={handleBlur('email')}
                                />
                                <Input
                                    title="Mot de passe"
                                    width="100%"
                                    value={values.password}
                                    onChange={handleChange('password')}
                                    placeholder="Mot de passe"
                                    type={hidePasswords ? "password" : "text"}
                                    isPassword
                                    hidePassword={hidePasswords}
                                    setHidePassword={(value: boolean) => setHidePasswords(value)}
                                    clearInput={() => handleChange('password')('')}
                                    hasError={(touched.password && errors.password) as boolean}
                                    onBlur={handleBlur('password')}
                                />
                                {isPending ?
                                    <div className={styles.forgotPasswordLoader}><Loader type="SMALL_SPINNER" /></div>
                                    :
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            mutate();
                                        }} 
                                        className={styles.forgotPassword}
                                        type="button"
                                    >
                                        Mot de passe oublié ?
                                    </button>
                                }
                                <ValidateButton title="Valider" onClick={handleSubmit} disabled={!isValid || !values.email || !values.password} onLoading={isLoading} />
                            </Form>
                        )
                    }}
                </Formik>
            </div>
        </div>
    );
}
